import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Sparkle, TrendUp, UsersThree, Receipt } from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";

/**
 * FarmersRevenueAdmin — admin-only dashboard for Farmers Pulse revenue.
 *
 * Backend: GET /api/billing/admin/revenue (403 for non-admins).
 *
 * Shows MRR, subscriber counts by plan, new signups this month and total
 * paid transactions.
 */
export default function FarmersRevenueAdmin() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    http.get("/billing/admin/revenue")
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Access denied"));
  }, []);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-revenue-admin-page">
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <header>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Sparkle size={12} weight="fill" className="inline mr-1" /> Admin · Revenue
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
            Farmers Pulse revenue
          </h1>
          <p className="mt-2 text-[#3A3830]">
            Recurring subscribers, MRR, and this month's signup pace.
          </p>
        </header>

        {err && (
          <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
            {err}
          </div>
        )}

        {d && (
          <>
            <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="farmers-revenue-tiles">
              <Tile Icon={Coins}     label="MRR"                    value={`$${d.mrr_usd.toLocaleString()}`} sub="Monthly recurring" />
              <Tile Icon={UsersThree} label="Active subscribers"     value={sumBy(d.by_plan, ["grower", "business", "cooperative"])} sub="Grower + Business + Coop" />
              <Tile Icon={TrendUp}   label="New this month"         value={d.new_this_month} sub="Signups since 1st" />
              <Tile Icon={Receipt}   label="Total paid transactions" value={d.total_paid_transactions} sub="All time" />
            </section>

            <section className="mt-8" data-testid="farmers-revenue-by-plan">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">By plan</div>
              <h2 className="mt-1 font-serif-display text-2xl">Subscribers per plan</h2>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                {["free", "grower", "business", "cooperative"].map((p) => (
                  <div key={p} className="rounded-2xl border border-black/10 bg-white p-4">
                    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{p}</div>
                    <div className="mt-1 font-serif-display text-3xl">{d.by_plan[p] || 0}</div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}

function Tile({ Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
        <Icon size={14} weight="fill" />
        <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
      </div>
      <div className="mt-1 font-serif-display text-3xl">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-[#3A3830]">{sub}</div>}
    </div>
  );
}

function sumBy(obj, keys) {
  return keys.reduce((s, k) => s + (obj[k] || 0), 0);
}
